import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import { first } from "@ckeditor/ckeditor5-utils";
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed';
import MediaStyleCommand from './mediastylecommand';  // Importamos el comando que definimos
import { icons } from '@ckeditor/ckeditor5-core';

// Definir el plugin MediaStyleEditing
export default class MediaStyleEditing extends Plugin {
    static get requires() {
        return [MediaEmbed];  // Aseguramos que los plugins requeridos estén presentes
    }

    static get pluginName() {
        return 'MediaStyleEditing';  // Nombre del plugin
    }

    init() {
        const editor = this.editor;
        const { objectFullWidth, objectLeft, objectRight } = icons;
        const mediaStyles = [
            {
                icon: objectLeft,
                name: 'side-left',
                title: 'Align left',
                className: 'media-style-side-left'
            },
            {
                icon: objectFullWidth,
                name: 'full',
                title: 'Full width',
                className: 'media-style-full-width'  // Aseguramos que la clase esté definida
            },
            {
                icon: objectRight,
                name: 'side-right',
                title: 'Align right',
                className: 'media-style-side-right'
            },
        ];

        // Registrar los estilos en el sistema de conversión para upcast y downcast
        const schema = editor.model.schema;
        const modelToViewConverter = modelToViewStyleAttribute(mediaStyles);
        const viewToModelConverter = viewToModelStyleAttribute(mediaStyles);
        editor.editing.downcastDispatcher.on('attribute:mediaStyle', modelToViewConverter);
        editor.data.downcastDispatcher.on('attribute:mediaStyle', modelToViewConverter);

        schema.extend('media', { allowAttributes: 'mediaStyle' });
        editor.data.upcastDispatcher.on('element:figure', viewToModelConverter, { priority: 'low' });
        editor.data.upcastDispatcher.on('element:oembed', viewToModelConverter, { priority: 'low' });

        // Añadir el comando para manejar los estilos
        editor.commands.add('mediaStyle', new MediaStyleCommand(editor));

        // Crear botones individuales para cada estilo y añadirlos al toolbar de mediaEmbed
        mediaStyles.forEach(style => {
            this._createStyleButton(style);
        });
    }

    // Función para crear los botones de estilo y añadirlos al toolbar
    _createStyleButton(style) {
        const editor = this.editor;

        // Registrar el botón en la barra de herramientas
        editor.ui.componentFactory.add(`mediaStyle:${style.name}`, locale => {
            const command = editor.commands.get('mediaStyle');
            const buttonView = new ButtonView(locale);

            buttonView.set({
                icon: style.icon,
                label: editor.t(style.title),
                tooltip: true,
                isToggleable: true
            });

            // Vincular el estado del botón al comando
            buttonView.bind('isEnabled').to(command, 'isEnabled');
            buttonView.bind('isOn').to(command, 'value', value => value === style.name);

            // Ejecutar el comando al hacer clic en el botón
            buttonView.on('execute', () => {
                editor.execute('mediaStyle', { value: style.name });
                editor.editing.view.focus();
            });

            return buttonView;
        });
    }
}

function modelToViewStyleAttribute(styles) {
    return (evt, data, conversionApi) => {
        if (!conversionApi.consumable.consume(data.item, evt.name)) {
            return;
        }
        const newStyle = getStyleDefinitionByName(data.attributeNewValue, styles);
        const oldStyle = getStyleDefinitionByName(data.attributeOldValue, styles);
        const viewElement = conversionApi.mapper.toViewElement(data.item);
        const viewWriter = conversionApi.writer;
        if (oldStyle) {
            viewWriter.removeClass(oldStyle.className, viewElement);
        }
        if (newStyle) {
            viewWriter.addClass(newStyle.className, viewElement);
        }
    };
}
function viewToModelStyleAttribute(styles) {
    return (evt, data, conversionApi) => {
        if (!data.modelRange) {
            return;
        }
        const viewElement = data.viewItem;
        const modelMediaElement = first(data.modelRange.getItems());
        if (!modelMediaElement) {
            return;
        }

        for (const style of styles) {
            if (style &&
                conversionApi.consumable.consume(viewElement, {
                    classes: style.className,
                })) {
                conversionApi.writer.setAttribute("mediaStyle", style.name, modelMediaElement);
            }
        }
    };
}
function getStyleDefinitionByName(name, styles) {
    for (const style of styles) {
        if (style.name === name) {
            return style;
        }
    }
}